<template>
  <div
    id="category"
    ref="productList"
    :data-ab-id="dataAbId"
  >
    <KikBreadcrumbs
      v-if="showBreadcrumb"
      :breadcrumbs="breadcrumbsToDisplay"
      class="mobile:!inline-flex"
    />
    <div class="flex w-full items-start mobile:flex-col">
      <div
        v-if="categoryTree"
        class="sticky top-10 mr-12 flex-[0_0_20%] mobile:hidden"
      >
        <p
          v-if="isSearch || breadcrumbsToDisplay?.[0]?.text"
          class="relative mb-8 mt-2 text-xl font-bold"
        >
          <span class="absolute mt-[-6.5px] size-[37px] border-[3px] border-primary-700" />
          <span class="relative ml-3 block bg-white uppercase leading-[25px]">{{ isSearch ? $i18n.t('Search') : breadcrumbsToDisplay?.[0]?.text }}</span>
        </p>
        <template
          v-if="isSearch"
        >
          <ul>
            <CategoryTree
              v-for="cat in searchCategories"
              :key="cat.value"
              :node="cat"
              :path="cat.value"
              :selected-paths="selectedPaths"
              :initial-value="getSelectedFilterValue('category_path')"
              @update:selected-paths="selectedPaths = $event"
            />
          </ul>
        </template>
        <template
          v-else
        >
          <SfAccordionItem
            v-for="(cat, i) in categoryTree.childrenItems"
            :key="cat.name"
            :model-value="isOpenCategoryAccordion(cat.name)"
            @update:model-value="toggleCategoryAccordion(i, $event, cat.name)"
          >
            <template #summary>
              <div class="mb-1 flex justify-between px-2 py-1 font-bold hover:text-primary-700">
                {{ cat.name }}
              </div>
            </template>
            <ul>
              <li
                v-for="subCat in cat.childrenItems"
                :key="subCat.name"
                class="ml-5"
              >
                <NuxtLink
                  v-if="isOpenCategoryAccordion(cat.name)"
                  :key="subCat.name"
                  :to="localePath(th.getCatLink([categoryTree, subCat]))"
                  class="mb-1 block px-2 py-1 text-base text-neutral-400 hover:text-primary-700"
                  :class="{ '!font-bold !text-neutral-900': isParentCategory(subCat.name), '!font-normal !text-primary-700': isActiveCategory(subCat.name) }"
                >
                  {{ subCat.name }}
                </NuxtLink>
                <CategoryMenuItem
                  v-if="isParentCategory(subCat.name)"
                  :parent-category="categoryTree"
                  :category="subCat"
                />
              </li>
            </ul>
          </SfAccordionItem>
        </template>
      </div>
      <div class="w-full grow">
        <div class="mb-4 flex flex-col mobile:px-4 desktop:flex-row desktop:items-end">
          <div class="flex w-full items-end justify-between">
            <div
              v-if="isLoadingCategory"
              class="h-8 w-40 animate-pulse rounded-md bg-gray-200 desktop:mr-3"
            />
            <KikHeading
              v-else
              :level="1"
              :title="categoryTitle"
              :title-class="'text-[30px] leading-[34px] mobile:text-[20px] mobile:leading-[24px] mr-2'"
            />
            <div
              v-if="isLoadingProducts"
              class="h-5 w-20 animate-pulse rounded-md bg-gray-200 desktop:mr-3"
            />
            <template v-else>
              <p class="text-nowrap text-base leading-5 text-neutral-900 desktop:mr-3">
                {{ pagination.totalItems }} {{ $t('Articles') }}
              </p>
            </template>
          </div>
          <div class="mt-4 tablet:hidden desktop:hidden">
            <KikCarousel
              previous-button-class="!relative !block !left-0 mr-1"
              next-button-class="!relative !block !right-0 ml-1"
            >
              <NuxtLink
                v-for="cat in category?.childrenItems"
                :key="cat.id"
                class="relative m-0 flex shrink-0 justify-start gap-5 underline"
                :to="localePath(th.getCatLink([categoryTree, cat as NavbarCategory]))"
              >
                {{ cat.name }}
              </NuxtLink>
            </KikCarousel>
          </div>
          <div class="mt-4 flex items-end gap-3 desktop:mt-0">
            <KikDropdown
              class="w-full desktop:min-w-48"
              title="Sort by"
              :options="sortOptions"
              @on-apply="selectSortOption"
            />
            <KikButton
              v-if="showFilters"
              class="relative mr-3 w-full !p-2 mobile:mr-0 desktop:min-w-48"
              :aria-label="$t('Filter & Sort')"
              variant="primary"
              @click="toggleFilterSidebar"
            >
              <SfIconTune class="text-white" />
              {{ $t('Filter') }}
              <span
                v-if="selectedFiltersTransformed.length"
                class="size-5 rounded-2xl bg-white text-xs leading-5 text-neutral-800"
              >{{ selectedFiltersTransformed.length }}</span>
            </KikButton>
          </div>
        </div>
        <div
          v-if="selectedFiltersTransformed.length && !isLoadingProducts"
          class="mb-4 flex flex-wrap items-start gap-2 px-4 desktop:px-0"
        >
          <div class="rounded border border-neutral-700 px-2 py-1 text-sm">
            {{ $t('Clear all filters') }}
            <SfIconClose
              class="ml-2 cursor-pointer text-neutral-900"
              size="sm"
              tabindex="0"
              @click="removeAllFilters"
              @keydown.space.prevent="removeAllFilters"
              @keydown.enter.prevent="removeAllFilters"
            />
          </div>
          <div
            v-for="filter in selectedFiltersTransformed"
            :key="filter"
            class="rounded border border-neutral-700 px-2 py-1 text-sm"
          >
            {{ filter.value }}
            <SfIconClose
              class="ml-2 cursor-pointer text-neutral-900"
              size="sm"
              tabindex="0"
              @click="removeSelectedFilter(filter.key)"
              @keydown.space.prevent="removeSelectedFilter(filter.key)"
              @keydown.enter.prevent="removeSelectedFilter(filter.key)"
            />
          </div>
        </div>
        <KikSkeletonProductCard
          v-if="!pages.length && isLoadingProducts"
          :height="isMobile ? '460px' : '680px'"
          :wrap="!isMobile"
          :number-per-line="isMobile ? 2 : 3"
          :number="36"
        />
        <transition-group
          v-for="{ id: pId, items } in pages"
          v-else
          :key="pId"
          appear
          name="products__slide"
          tag="div"
          class="-mt-px flex flex-wrap border-l border-l-neutral-200 mobile:-mr-px mobile:border-l-0"
          enter-active-class="transition duration-150 ease-in"
          enter-from-class="transform opacity-0 scale-50"
          enter-to-class="opacity-100"
          :data-page="pId"
          @after-enter="onAfterEnter"
        >
          <KikProductCard
            v-for="(product, i) in items"
            :id="product.id"
            :key="product.id"
            :product="product"
            :teaser-amount="4"
            :loading="getImageLoadType(i)"
          />
        </transition-group>
        <div
          v-if="hasMoreItems"
          class="mt-8 flex justify-center"
        >
          <KikButton
            class="mt-4 min-w-64"
            variant="secondary"
            :uppercase="true"
            @click="onShowMore"
          >
            <SfLoaderCircular v-if="isLoadingMore" />
            <span v-else>{{ $t('Load more products') }}</span>
          </KikButton>
        </div>
        <div
          v-if="pagination && pagination.totalPages > 1"
          class="mt-10 flex w-full justify-center"
        >
          <div class="w-[550px] mobile:w-full">
            <KikPagination
              :key="pagination.totalPages"
              :total="pagination.totalItems"
              :current="pagination.currentPage"
              @click:navigate="onNavigate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <FiltersMenu
    v-if="showFilters"
    :page-id="pageId"
    :categories="categories"
    :products="products"
    :product-count="pagination?.totalItems"
    @on-apply-filter="onFetchCategory"
    @close-sidebar="closeFilterSidebar"
  />
</template>

<script lang="ts" setup>
import {
  SfAccordionItem,
  SfIconTune,
  SfLoaderCircular,
  SfIconClose,
} from '@storefront-ui/vue';
import { chunk } from 'lodash-es';
import CategoryTree from './CategoryTree.vue';
import { useBreadcrumbs } from '~/composables/useBreadcrumb/useBreadcrumb';
import { useProduct } from '~/composables/custom/useProduct/useProduct';
import useUiHelpers from '~/composables/useUiHelpers';
import { useFilters } from '~/composables/custom/useFilters';
import FiltersMenu from '~/components/Category/FiltersMenu.vue';
import { useTracking } from '~/composables/custom/useTracking';
import { useScrollStore } from '~/store/scroll';
import { replaceHistory } from '~/helpers/utils/replaceHistory';
import { useMagnolia } from '~/composables/custom/useMagnolia/useMagnolia';
import { useTagsLoader } from '~/composables/custom/useTagLoader/useTagLoader';
import { useSSR } from '~/composables/useSSR/useSSR';
import { useFocusStore } from '~/store/focus';

const { restoreFocus, clearFocus } = useFocusStore();

interface Props {
  byCategories?: string[];
  bySkus?: string[];
  byProducts?: string[];
  byAttributes?: any;
  showCategories?: boolean;
  showFilters?: boolean;
  id: string;
  page?: number;
  textSearch?: string;
  categoryTree?: NavbarCategory;
  scrollTo?: string;
  operator?: string;
  dataAbId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  byCategories: undefined,
  bySkus: undefined,
  byProducts: undefined,
  byAttributes: undefined,
  showCategories: true,
  showFilters: true,
  page: 1,
  textSearch: undefined,
  categoryTree: undefined,
  scrollTo: undefined,
});

const {
  id: pageId,
  page: currentPage,
  textSearch: searchQuery,
  byCategories: categories,
  showFilters,
  categoryTree,
} = toRefs(props);

const { $config, $i18n } = useNuxtApp();
const { isMobile } = useDevice();
const { plpSearch, trackViewItemList, trackViewSearch } = useTracking();
const th = useUiHelpers();
const { isDesktop } = useDevice();
const { getProducts, loading: isLoadingProducts, getSearchResults, searchCategories } = useProduct();
const { toggleFilterSidebar, closeFilterSidebar, isFilterSidebarOpen } = useUiState();
const { getFilters, getSelectedFilterValue, selectedFiltersTransformed, removeFilter, removeFilters, sortOptions, applyFilter, headlineColorSelected, setFilters } = useFilters();
const localePath = useLocalePath();
const { result: category, loading: isLoadingCategory, load: loadCategory, setCategoryMeta } = useCategory();
const { breadcrumbsToDisplay, breadcrumbs } = useBreadcrumbs();
const { fetchTags } = useTagsLoader();
const { onSSR } = useSSR();
const { atMagnolia } = useMagnolia();

const route = useRoute();
const searchValue = computed(() => route.query.search);
const { isSearch } = route.meta as { isSearch: boolean };

const products: Ref<any[]> = useState(`list-of-products-${pageId.value}`, () => []);

const pagination = useState(`pagination-plp-${pageId.value}`, () => ({
  previous: null,
  topPage: null,
  currentPage: currentPage.value ?? 1,
  totalPages: 0,
  totalItems: 0,
  itemsPerPage: 0,
}));

const scroll = useScrollStore();
const searchQueryItems = computed(() => getFilters(props.byAttributes)?.search);

const opened = ref<number | null>(null);
const productList = ref();

const openCategoryAccordion = computed(() =>
  !searchQuery.value ? breadcrumbs.value?.[1]?.name : breadcrumbs.value?.[0]?.name,
);

const selectedPaths = ref(new Set());

watch(selectedPaths, async (newVal) => {
  applyFilter('category_path', Array.from(newVal).join(','));
  await onFetchCategory();
});

const isOpenCategoryAccordion = (categoryName: string) => {
  return openCategoryAccordion.value === categoryName;
};

const toggleCategoryAccordion = (id: number, open: boolean, categoryName: string) => {
  if (open) {
    opened.value = id;
    openCategory(categoryName);
  }
  else if (isOpenCategoryAccordion(categoryName)) {
    opened.value = null;
    openCategory(categoryName);
  }
};

const handleRedirection = ({ statusCode, redirectUrl }: { statusCode: number; redirectUrl: string }) => {
  if ([301, 302].includes(statusCode)) {
    navigateTo(redirectUrl, { redirectCode: statusCode });
  }
  else if (statusCode === 410) {
    navigateTo('', { redirectCode: statusCode });
  }
  else if (statusCode === 404) {
    navigateTo('', { redirectCode: statusCode });
  }
};

const fetchSearchResults = async () => {
  const limit = isDesktop ? $config.public.category?.itemsPerPage?.desktop : $config.public.category?.itemsPerPage?.mobile;
  applyFilter('search', searchQueryItems.value);
  const {
    total = 0,
    items = [],
    filters: currentFilters,
  } = await getSearchResults(searchQueryItems.value as string, pagination.value.currentPage, {
    returnFacets: true,
    filterAndSortBy: getFilters({ ...props.byAttributes }),
  });

  const cleanedFilters = Object.fromEntries(Object.entries(currentFilters).filter(([_, value]) => Boolean(value)));

  if (searchQueryItems.value) {
    plpSearch(searchQueryItems.value as string);
    trackViewSearch(searchQueryItems.value as string, items, cleanedFilters);
  }

  handleFilterAndPagination(limit, total, items, currentFilters);

  if (isFilterSidebarOpen.value) {
    restoreFocus();
    clearFocus();
  }
};

const fetchCategory = async () => {
  if ((category.value)?.redirectionRules?.statusCode && category.value?.redirectionRules?.statusCode > 300) {
    handleRedirection(category.value?.redirectionRules);
    return;
  }

  if (isSearch && import.meta.client) {
    await fetchSearchResults();
    return;
  }

  const limit = isDesktop ? $config.public.category?.itemsPerPage?.desktop : $config.public.category?.itemsPerPage?.mobile;
  const {
    total = 0,
    items = [],
    filters: currentFilters,
  } = (await getProducts({
    products: props.byProducts,
    categories: categories.value,
    skus: props.bySkus,
    offset: (pagination.value.currentPage - 1) * limit,
    limit,
    returnFacets: true,
    filterAndSortBy: getFilters(props.byAttributes),
    withVariants: false,
    operator: props.operator,
  })) || {};

  if (category.value) {
    trackViewItemList(category.value, items);
  }

  handleFilterAndPagination(limit, total, items, currentFilters);

  if (isFilterSidebarOpen.value) {
    restoreFocus();
    clearFocus();
  }
};

const handleFilterAndPagination = (limit: number, total: number, items: any, currentFilters: any) => {
  pagination.value.itemsPerPage = limit;
  pagination.value.totalItems = total;
  pagination.value.totalPages = Math.ceil(total / limit);
  products.value = [...products.value, ...(items || [])];

  if (props.byAttributes) {
    Object.keys(props.byAttributes)?.forEach((key) => {
      if (currentFilters?.[key]) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete currentFilters[key];
      }
    });
  }
  setFilters({
    ...currentFilters,
    hide: {
      recent: props.byAttributes.recent !== undefined,
      sale: props.byAttributes.sale !== undefined,
      mykik: props.byAttributes.mykik !== undefined,
    },
  });
};

const pages = computed(() =>
  chunk(products.value, pagination.value.itemsPerPage).map((items, index) => ({
    id: (pagination.value.topPage ?? 0) + index,
    items,
  })));

const isParentCategory = (name: string) => breadcrumbs.value?.some(breadcrumb => breadcrumb.name === name);
const isActiveCategory = (name: string) => (breadcrumbs.value?.[breadcrumbs.value.length - 1]?.name === name);

const openCategory = (categoryName: string) => {
  const currentItem = props.categoryTree?.childrenItems?.find(item => item.name === categoryName);
  if (!currentItem || !props.categoryTree) {
    return;
  }
  navigateTo(localePath(th.getCatLink([props.categoryTree, currentItem])));
};

const isLoadingMore = ref(false);

const onShowMore = async () => {
  isLoadingMore.value = true;
  pagination.value.currentPage += 1;
  await fetchCategory();
  addQueryParams({ page: pagination.value.currentPage });
  isLoadingMore.value = false;
};

const deleteQueryParams = (...params: any[]) => {
  const searchParams = new URLSearchParams(window.location.search);

  params.forEach((key) => {
    searchParams.delete(key);
  });
  replaceHistory(window.location.pathname, searchParams);
};

const addQueryParams = (params: any) => {
  const searchParams = new URLSearchParams(window.location.search);

  Object.keys(params).forEach((key) => {
    searchParams.set(key, params[key]);
  });

  replaceHistory(window.location.pathname, searchParams);
};

const onAfterEnter = () => {
  if (history.scrollRestoration) {
    history.scrollRestoration = 'manual';
  }
  if (!scroll.scrollTo) {
    return;
  }
  const elementById = document.getElementById(scroll.scrollTo);
  if (!elementById) {
    return;
  }

  scroll.setScroll('');

  const yOffset = window.innerHeight / 3;
  const y = (elementById?.getBoundingClientRect()?.top || 0) + window.scrollY - yOffset;

  scrollToY(y);
};

onSSR(async () => {
  if (atMagnolia.value) {
    return;
  }
  if (!products.value?.length) {
    pagination.value.currentPage = currentPage.value ?? 1;
    await loadCategory();
    await fetchCategory();
  }
  await fetchTags();
});

onBeforeMount(async () => {
  if (atMagnolia.value) {
    if (!products.value?.length) {
      pagination.value.currentPage = currentPage.value ?? 1;
      await fetchCategory();
    }
  }
  setCategoryMeta(category.value);
});

watch(category, (newCategory) => {
  if (category.value && category.value?.id !== newCategory?.id) {
    trackViewItemList(category.value, products.value);
  }
});

onMounted(async () => {
  if (searchQuery.value) {
    plpSearch(searchQuery.value);
  }

  if (pagination.value.currentPage > 1) {
    addQueryParams({ page: pagination.value.currentPage });
  }
  if (category.value) {
    trackViewItemList(category.value, products.value);
  }
});

onUnmounted(() => {
  products.value = [];
  pagination.value.currentPage = 1;
  selectedPaths.value.clear();
});

watch(searchQueryItems, async () => {
  products.value = [];
  selectedPaths.value.clear();
  await fetchCategory();
  setCategoryMeta(category.value);
});

watch(searchValue, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    pagination.value.currentPage = 1;
    selectedPaths.value.clear();
    setCategoryMeta(category.value);
  }
});

const scrollToY = (position: number) => {
  window.scrollTo({ top: position, left: 0, behavior: 'smooth' });
};

const selectSortOption = async (option: any) => {
  applyFilter('sort', option);
  await onFetchCategory();
};

const removeAllFilters = async () => {
  await removeFilters(categories.value ?? [], products.value);
  await onFetchCategory();
};

const removeSelectedFilter = async (key: string) => {
  await removeFilter(key, categories.value ?? [], products.value);
  await onFetchCategory();
};

const onFetchCategory = async (fetchProducts = true, page = 1, useOldIfAvailable = false) => {
  setTimeout(() => productList.value?.scrollIntoView({ behavior: 'smooth' }));
  products.value = useOldIfAvailable ? products.value.filter(product => product.page === page) : [];
  pagination.value.currentPage = page;

  if (page === 1) {
    deleteQueryParams('page');
  }
  else {
    addQueryParams({ page });
  }

  if (!products.value.length && fetchProducts) {
    await fetchCategory();
  }
};

const onNavigate = (page: number) => onFetchCategory(true, page, true);
const hasMoreItems = computed(() => pagination.value.totalItems > products.value?.length && pagination.value.currentPage < pagination.value.totalPages);

const getImageLoadType = (index: number): HTMLImageElement['loading'] => {
  const threshold = isMobile ? 4 : 6;
  return index < threshold ? 'eager' : 'lazy';
};

const categoryTitle = computed(() => {
  if (searchQueryItems.value) {
    return `'${searchQueryItems.value}'`;
  }
  let title = '';

  if (route.meta?.isNew && category.value) {
    title = category.value.key == 'ShopNavigation' ? `${$i18n.t('New Collection')}` : `${$i18n.t('New Collection')}: ${category.value.name}`;
  }
  else if (route.meta?.isSale && (route.params.slug_1 || route.params.type)) {
    title = category.value?.saleMetadata.headline || `${$i18n.t('Sale')}: ${category.value?.headlineTitle ?? category.value?.name}`;
  }
  else {
    title = category.value?.headlineTitle ?? category.value?.name ?? '';
  }

  if (!route.meta?.isNew && title && headlineColorSelected.value) {
    title = `${title} in ${headlineColorSelected.value}`;
  }

  return title;
});

// If not a regular PLP then we don't want to show breadcrumbs
const showBreadcrumb = computed(() => route.name?.toString()?.startsWith('category') && route.params.slug_1);
</script>
